import {useEffect, type ChangeEvent } from 'react'
import styled from 'styled-components'
import type { FormikProps } from 'formik'
import { useTranslation } from 'react-i18next'
import {
  AtlasBodySubtitle,
  AtlasBodyTitle,
  AtlasColor,
  CardComponent,
  PrimaryButtonAtom
} from '@justomx/atlas-components'
import { Icon } from '@justomx/atlas-icons'
import { FlexRowAtom } from 'components/atoms/FlexRow.atom'
import type { FormikLotFormValues, ProductDetail } from 'types/receipt/model'
import { FlexColumnAtom } from 'components/atoms/FlexColumn.atom'
import { FabButtonAtom } from 'components/atoms/FabButton.atom'
import { FormikTextFieldOrganism } from './FormikTextField.organism'
import { MEASURE_UNITS } from 'utils/constants'

interface LotFormProps {
  formConfig: FormikProps<FormikLotFormValues>
  containerName: string
  product: ProductDetail
  remove: <X = any>(index: number) => X | undefined
  onPrint: (index: number) => void
}

const CardHeader = styled(FlexRowAtom)`
  justify-content: space-between;
  align-items: center;
`

export const LotFormOrganism = (props: LotFormProps) => {
  const { t } = useTranslation('global')
  const { formConfig, containerName, product, remove, onPrint } = props

  const onBoxesChange = async (
    event: ChangeEvent<HTMLInputElement>,
    boxesFieldName: string,
    affectedFieldName: string
  ) => {
    const inputValue = event.target.value.trim()

    if (inputValue === '') {
      await formConfig.setFieldValue(boxesFieldName, '')
      if (product.measureUnit !== 'KG') {
        await formConfig.setFieldValue(affectedFieldName, '')
      }
      return
    }

    const numericValue = parseInt(inputValue, 10)
    if (isNaN(numericValue) || numericValue < 0) return

    await formConfig.setFieldValue(boxesFieldName, numericValue)

    if (product.measureUnit !== 'KG') {
      const calculatedValue = numericValue * product.unitsOfMeasurment
      await formConfig.setFieldValue(affectedFieldName, calculatedValue)
    }
  }

  const onDateChange = async (
    event: ChangeEvent<HTMLInputElement>,
    name: string
  ) => {
    let inputValue = event.target.value.replace(/\//g, '')
    if (inputValue.length >= 2 && inputValue.length <= 4) {
      inputValue =
        inputValue.substring(0, 2) +
        '/' +
        inputValue.substring(2) +
        (inputValue.length === 4 ? '/' : '')
    } else if (inputValue.length > 4) {
      inputValue =
        inputValue.substring(0, 2) +
        '/' +
        inputValue.substring(2, 4) +
        '/' +
        inputValue.substring(4)
    }
    await formConfig.setFieldValue(name, inputValue)
  }

  useEffect(() => {
    if (product.zone === 'Granel') {
      formConfig.values.forms.forEach((_, index) => {
        const fieldName = `forms[${index}].amountOfBoxes`;
        formConfig.setFieldValue(fieldName, '1');
      });
    }
  }, [product.zone, formConfig.values.forms.length]);

  return (
    <FlexColumnAtom $gap={16}>
      {formConfig.values.forms.map((form, index) => (
        <CardComponent key={index} style={{ gap: '24px' }}>
          <CardHeader>
            <FlexColumnAtom>
              <AtlasBodyTitle>
                {t('receipt-lot-form.lot-number', { lotNumber: index + 1 })}
              </AtlasBodyTitle>
              <AtlasBodySubtitle>
                {form.containerName || containerName}
              </AtlasBodySubtitle>
            </FlexColumnAtom>

            <FabButtonAtom
              $variant="secondary"
              $size={40}
              type="button"
              disabled={index === 0}
              onClick={() => {
                remove(index)
              }}
            >
              <Icon
                name="ic_delete"
                color={index === 0 ? AtlasColor.BoxShadow : AtlasColor.Primary}
                size={20}
              />
            </FabButtonAtom>
          </CardHeader>

          <FlexColumnAtom $gap={24}>
            <FormikTextFieldOrganism
              type="text"
              name={`forms[${index}].lot`}
              label={t('receipt-lot-form.lot')}
              formConfig={formConfig}
            />

            <FormikTextFieldOrganism
              type="text"
              name={`forms[${index}].expirationDate`}
              label={t('receipt-lot-form.expiration')}
              formConfig={formConfig}
              customChange={async (event) => {
                await onDateChange(event, `forms[${index}].expirationDate`)
              }}
            />

            {product.measureUnit === MEASURE_UNITS.box && (
              <>
                <FormikTextFieldOrganism
                  type="number"
                  name={`forms[${index}].amountOfBoxes`}
                  label={t('receipt-lot-form.boxes')}
                  formConfig={formConfig}
                  customChange={async (event) => {
                    await onBoxesChange(
                      event,
                      `forms[${index}].amountOfBoxes`,
                      `forms[${index}].numberOfUnits`
                    )
                  }}
                />
                <FormikTextFieldOrganism
                  type="number"
                  name={`forms[${index}].numberOfUnits`}
                  label={t(
                    product.measureUnit === 'KG'
                      ? 'receipt-lot-form.grams'
                      : 'receipt-lot-form.units'
                  )}
                  supportingText={t('receipt-lot-form.units-hint', {
                    quantity: Number(form.amountOfBoxes) || 0,
                    units:
                      product.measureUnit !== 'KG'
                        ? (Number(form.amountOfBoxes) || 0) *
                          product.unitsOfMeasurment
                        : 0
                  })}
                  formConfig={formConfig}
                  disabled={product.measureUnit !== 'KG'}
                />
              </>
            )}

            {product.measureUnit !== MEASURE_UNITS.box && (
              <>
                <FormikTextFieldOrganism
                  type="number"
                  name={`forms[${index}].numberOfUnits`}
                  label={t(
                    product.measureUnit === 'KG'
                      ? 'receipt-lot-form.grams'
                      : 'receipt-lot-form.units'
                  )}
                  formConfig={formConfig}
                />
                {product.zone !== 'Granel' && (
                <FormikTextFieldOrganism
                  type="number"
                  name={`forms[${index}].amountOfBoxes`}
                  label={t('receipt-lot-form.boxes')}
                    formConfig={formConfig}
                  />
                )}
              </>
            )}
          </FlexColumnAtom>

          <PrimaryButtonAtom
            type="button"
            style={{ backgroundColor: '#FFEEB6', display: 'flex', gap: '12px' }}
            onClick={() => {
              onPrint(index)
            }}
          >
            <Icon name="ic_print" /> imprimir
          </PrimaryButtonAtom>
        </CardComponent>
      ))}
    </FlexColumnAtom>
  )
}
